import { t } from "@lingui/macro";
import { BarChart3, FileCheck, Sparkles, Target, Zap } from "lucide-react";

import { GlowingEffect } from "@/client/components/glowing-effect";

export function HomeServices() {
  return (
    <section id="how-it-works" className="py-10">
      <ul className="container grid grid-cols-1 grid-rows-none gap-4 md:grid-cols-12 md:grid-rows-3 lg:gap-4 xl:max-h-[34rem] xl:grid-rows-2">
        <GridItem
          area="md:[grid-area:1/1/2/7] xl:[grid-area:1/1/2/5]"
          icon={<Target className="size-4 text-black dark:text-neutral-400" />}
          title={t`Guaranteed ATS Optimization`}
          description={t`Our smart algorithms analyze your resume in real-time to ensure an ATS optimization score above 87%.`}
        />

        <GridItem
          area="md:[grid-area:1/7/2/13] xl:[grid-area:2/1/3/5]"
          icon={<Sparkles className="size-4 text-black dark:text-neutral-400" />}
          title={t`Recruiter-approved Templates`}
          description={t`Developed in collaboration with HR professionals, our templates are designed to make a strong first impression.`}
        />

        <GridItem
          area="md:[grid-area:2/1/3/7] xl:[grid-area:1/5/3/8]"
          icon={<Zap className="size-4 text-black dark:text-neutral-400" />}
          title={t`AI-assisted Writing`}
          description={t`Our AI analyzes your background and suggests impactful phrasing that transforms your experience into an impact story.`}
        />

        <GridItem
          area="md:[grid-area:2/7/3/13] xl:[grid-area:1/8/2/13]"
          icon={<FileCheck className="size-4 text-black dark:text-neutral-400" />}
          title={t`Smart Job Offer Adaptability`}
          description={t`Customize your resume with one click for each job offer - our system automatically extracts relevant keywords.`}
        />

        <GridItem
          area="md:[grid-area:3/1/4/13] xl:[grid-area:2/8/3/13]"
          icon={<BarChart3 className="size-4 text-black dark:text-neutral-400" />}
          title={t`Real-time Performance Analysis`}
          description={t`Track your resume's open rate and receive improvement tips based on job market data.`}
        />
      </ul>
    </section>
  );
}

type GridItemProps = {
  area: string;
  icon: React.ReactNode;
  title: string;
  description: React.ReactNode;
};

const GridItem = ({ area, icon, title, description }: GridItemProps) => {
  return (
    <li className={`min-h-56 list-none ${area}`}>
      <div className="rounded-2.5xl relative h-full border p-2 md:rounded-3xl md:p-3">
        <GlowingEffect
          spread={40}
          glow={true}
          disabled={false}
          proximity={64}
          inactiveZone={0.01}
        />
        <div className="border-0.75 relative flex h-full flex-col justify-between gap-6 overflow-hidden rounded-xl p-6 dark:shadow-[0px_0px_27px_0px_#2D2D2D] md:p-6">
          <div className="relative flex flex-1 flex-col justify-between gap-3">
            <div className="w-fit rounded-lg border border-gray-600 p-2">{icon}</div>
            <div className="space-y-3">
              <h3 className="-tracking-4 text-balance pt-0.5 text-xl/[1.375rem] font-semibold text-black dark:text-white md:text-2xl/[1.875rem]">
                {title}
              </h3>
              <h2 className="text-sm/[1.125rem] text-black dark:text-neutral-400 md:text-base/[1.375rem] [&_b]:md:font-semibold [&_strong]:md:font-semibold">
                {description}
              </h2>
            </div>
          </div>
        </div>
      </div>
    </li>
  );
};
